import { useEffect, useRef, useState, type RefObject } from 'react';

export type MicStatus = 'requesting' | 'running' | 'denied' | 'unsupported';

interface WebkitWindow {
  webkitAudioContext?: typeof AudioContext;
}

/**
 * Microphone loudness as a smoothed 0..1 level, delivered every animation
 * frame via `onLevel` (a ref, so the consumer can write straight to the DOM
 * without re-rendering each frame). Returns a coarse status for the UI.
 *
 * - Computes RMS from the time-domain waveform, normalizes to a perceptual
 *   0..1 range, and smooths with a fast attack / slow release so the meter
 *   jumps up promptly but eases down (reads calmer).
 * - Disables the browser's echo-cancel / noise-suppress / auto-gain so the
 *   level reflects the actual room, not a processed call signal.
 * - Tears everything down on disable/unmount: stops the mic tracks (so the
 *   OS mic indicator goes off) and closes the AudioContext.
 */
export function useMicLevel(
  enabled: boolean,
  onLevel: (level: number) => void,
  sensitivityRef?: RefObject<number>,
  attempt = 0,
): MicStatus {
  const [status, setStatus] = useState<MicStatus>('requesting');
  const onLevelRef = useRef(onLevel);
  onLevelRef.current = onLevel;

  useEffect(() => {
    if (!enabled) return;

    const AudioCtor: typeof AudioContext | undefined =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (window as unknown as WebkitWindow).webkitAudioContext;

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || !AudioCtor) {
      setStatus('unsupported');
      return;
    }

    let cancelled = false;
    let settled = false;
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let raf = 0;
    let smoothed = 0;

    setStatus('requesting');
    // If the permission/OS prompt never resolves (a common "stuck on requesting"
    // case on first use), surface a recoverable error instead of hanging.
    const timeout = setTimeout(() => {
      if (!cancelled && !settled) setStatus('denied');
    }, 10000);

    navigator.mediaDevices
      .getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      })
      .then((s) => {
        settled = true;
        clearTimeout(timeout);
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        ctx = new AudioCtor();
        void ctx.resume?.();
        const source = ctx.createMediaStreamSource(s);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.2;
        source.connect(analyser);
        const buffer = new Uint8Array(analyser.fftSize);
        setStatus('running');

        const tick = () => {
          analyser.getByteTimeDomainData(buffer);
          let sumSquares = 0;
          for (let i = 0; i < buffer.length; i++) {
            const v = (buffer[i]! - 128) / 128;
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / buffer.length);
          // Normalize: a quiet room RMS is tiny; ~0.3 RMS is already very loud.
          // Per-device calibration scales it before clamping (read live from a
          // ref so the sensitivity slider takes effect without restarting).
          const sensitivity = sensitivityRef?.current ?? 1;
          const target = Math.min(1, rms * 3.2 * sensitivity);
          // Fast attack, slow release.
          const ease = target > smoothed ? 0.5 : 0.08;
          smoothed += (target - smoothed) * ease;
          onLevelRef.current(Math.max(0, Math.min(1, smoothed)));
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      })
      .catch(() => {
        settled = true;
        clearTimeout(timeout);
        if (!cancelled) setStatus('denied');
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (raf) cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close().catch(() => {});
    };
  }, [enabled, attempt]);

  return status;
}
